import { useMemo, useState } from 'react'
import { useQuery, gql } from '@apollo/client'
import { Button, Row, Col, Modal, ModalHeader, ModalBody } from 'reactstrap'
import TripCard from './TripCard'
import Loader from '../../../../../../Material/Loader'
import DateRangeSelector from './DateRangeSelector'
import TripsResultSummary from './TripsResultSummary'
import LifetimeTripsSummary from './LifetimeTripsSummary'
import TripDayRow from './TripDayRow'
import { GetConfigurationSettings } from "../../../../../../Functions/querys"
import { formatDate, formatDateLong, isValidDate, convertToTimezoneDate } from '../../../../../../utils'

const ALL_ARGYLE_GIGS = gql`
  query AllArgyleGigs(
    $driverId: ID!
    $startDate: Date
    $endDate: Date
    $limit: Int
  ) {
    allArgyleGigs(
      driverId: $driverId
      startDate: $startDate
      endDate: $endDate
      limit: $limit
    ) {
      gigId
      startDatetime
      endDatetime
      timezone
      incomeTotal
      incomeCurrency
      startLocationFormattedAddress
      endLocationFormattedAddress
      employer
      distance
      distanceUnit
      status
      type
    }
  }
`

const DRIVER_EARNINGS_SUMMARY = gql`
  query DriverEarningsSummary($driverId: ID!) {
    driverEarningsSummary(driverId: $driverId) {
      lifeTimeTripCount
      lifeTimeTotalEarnings
      averageEarningsPerTrip
      totalCompletedTrips
    }
  }
`

const USER_QUERY = gql`
  query UserQuery {
    currentUser {
      member {
        preferredTimezone
      }
    }
  }
`

function groupTripsByDay(trips, effectiveTimezone) {
    return trips.reduce((acc, trip) => {
        const tz = trip.timezone || effectiveTimezone || 'UTC'
        const date = convertToTimezoneDate(trip.startDatetime, tz)
        if (!acc[date]) acc[date] = []
        acc[date].push(trip)
        return acc
    }, {})
}

const Trips = ({ driverId }) => {
    const { data: userData } = useQuery(USER_QUERY, { fetchPolicy: 'cache-first' })
    const { data: branchData, loading: isBranchSettingsLoading } = useQuery(
        GetConfigurationSettings,
        {
            skip: !driverId,
            variables: { driverId, name: "PREFERRED_TIMEZONE" },
            fetchPolicy: "cache-first",
        }
    );
    const branchTimezone = branchData?.getConfigurationSettings || null
    const userTimezone = userData?.currentUser?.member?.preferredTimezone || null
    const effectiveTimezone = branchTimezone || userTimezone || 'UTC'

    const [endDate, setEndDate] = useState(() => formatDate(new Date()))
    const [startDate, setStartDate] = useState(() => {
        const date = new Date()
        date.setDate(date.getDate() - 6)
        return formatDate(date)
    })
    const [selectedDate, setSelectedDate] = useState(null)
    const [activePreset, setActivePreset] = useState('7d')

    const datesValid = isValidDate(startDate) && isValidDate(endDate)

    const { data, loading: gigsLoading, error } = useQuery(ALL_ARGYLE_GIGS, {
        skip: !datesValid,
        variables: {
            driverId,
            startDate,
            endDate,
            limit: 1000,
        },
    })

    const { data: lifetimeStats, loading: lifetimeStatsLoading } = useQuery(DRIVER_EARNINGS_SUMMARY, {
        variables: {
            driverId,
        },
        fetchPolicy: 'cache-first',
    })

    const groupedTrips = useMemo(() => {
        if (!data?.allArgyleGigs) return {}
        return groupTripsByDay(data.allArgyleGigs, effectiveTimezone)
    }, [data?.allArgyleGigs, effectiveTimezone])

    const dateRangeDays = useMemo(() => {
        if (!endDate || !startDate) return []
        const days = []
        const cursor = new Date(endDate + 'T00:00:00')
        const start = new Date(startDate + 'T00:00:00')
        while (cursor >= start) {
            days.push(formatDate(cursor))
            cursor.setDate(cursor.getDate() - 1)
        }
        return days
    }, [startDate, endDate])

    const allTrips = useMemo(() => {
        return dateRangeDays.flatMap(date => groupedTrips[date] || [])
    }, [groupedTrips, dateRangeDays])

    const uniqueDayCount = dateRangeDays.length

    if (isBranchSettingsLoading || lifetimeStatsLoading) return <Loader />

    return (
        <div>
            <h4>Trips</h4>
            <LifetimeTripsSummary stats={lifetimeStats?.driverEarningsSummary} />
            <Row className="mb-3 px-2 align-items-end flex-wrap gap-3">
                <Col className="d-flex flex-wrap align-items-end gap-2" style={{ flexGrow: 1 }}>
                    <DateRangeSelector
                        startDate={startDate}
                        endDate={endDate}
                        onStartChange={setStartDate}
                        onEndChange={setEndDate}
                        activePreset={activePreset}
                        onPresetSelect={setActivePreset}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <TripsResultSummary
                        trips={allTrips}
                        dateCount={uniqueDayCount}
                    />
                </Col>
            </Row>
            <div className="px-2">
                {error && <div className="text-danger">Error loading trips</div>}
                {gigsLoading ? (
                    <Loader />
                ) : (
                    <div className="overflow-auto" style={{ maxHeight: '400px' }}>
                        <Row className="align-items-center border-bottom py-2 mx-0">
                            <Col xs={4}>Date</Col>
                            <Col xs={5}>Trips & Earnings</Col>
                            <Col xs={3} className="align-items-center d-flex justify-content-end">
                                <div className="px-2">Actions</div>
                            </Col>
                        </Row>
                        {dateRangeDays
                            .filter(date => (groupedTrips[date]?.length ?? 0) > 0)
                            .map(date => (
                                <TripDayRow
                                    key={date}
                                    date={date}
                                    trips={groupedTrips[date]}
                                    onClickDetails={() => setSelectedDate(date)}
                                />
                            ))
                        }
                    </div>
                )}
                {!gigsLoading && dateRangeDays.every(date => !(groupedTrips[date]?.length > 0)) && (
                    <div className="text-muted text-center py-4">
                        No trips in selected date range.
                    </div>
                )}
                {!gigsLoading &&
                    <div className="text-center mt-4 justify-content-end d-flex">
                        <Button
                            size="sm"
                            color="secondary"
                            onClick={() => {
                                const date = new Date(startDate + 'T00:00:00')
                                date.setDate(date.getDate() - 7)
                                setStartDate(formatDate(date))
                                setActivePreset(null)
                            }}
                            disabled={gigsLoading}
                        >
                            Load more
                        </Button>
                    </div>
                }
            </div>
            <Modal isOpen={!!selectedDate} toggle={() => setSelectedDate(null)} size="lg">
                <ModalHeader toggle={() => setSelectedDate(null)}>
                    Trips on {selectedDate && formatDateLong(selectedDate)}
                </ModalHeader>
                <ModalBody>
                    {(selectedDate && groupedTrips[selectedDate]) ? (
                        groupedTrips[selectedDate].map((trip) => (
                            <TripCard key={trip.gigId} trip={trip} timezone={trip.timezone || effectiveTimezone} />
                        ))
                    ) : (
                        <div>No trips for this day.</div>
                    )}
                </ModalBody>
            </Modal>
        </div>
    )
}

export default Trips
